import { ILocationResponse } from '@kcftech/prometheus-client';
import { DocumentTitle } from 'components/DocumentTitle';
import { BaseLandingPage, MainContent, TopRow } from 'components/page/BaseLandingPage';
import { useUser } from 'contexts/UserContext';
import { useLocationIssueMetrics } from 'custom_hooks/data_hooks/location/useLocationIssueMetrics';
import {
    AlertsAndDraftsWidget,
    AlertsAndDraftsWidgetRef,
} from 'pages/Location/alerts_and_drafts/AlertsAndDraftsWidget';
import {
    LocationHardwareStatusWidget,
    LocationHardwareStatusWidgetRef,
} from 'pages/Location/hardware_status/LocationHardwareStatusWidget';
import {
    LocationIssuesWidget,
    LocationIssuesWidgetRef,
} from 'pages/Location/issues/LocationIssuesWidget';
import { LocationOverviewStoreProvider } from 'pages/Location/state/LocationOverviewStoreProvider';
import { LocationLeftColumn, LocationRightColumn } from 'pages/Location/styled';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IssueMetrics } from 'shared/issue_metrics/IssueMetricsWidget';
import { LastUpdated } from 'shared/last_updated/LastUpdated';
import { LandingPageLastUpdated } from 'shared/last_updated/styled';

export type LocationLandingPageProps = Readonly<{
    location: ILocationResponse;
}>;

// Define widget types for better type safety
type WidgetName = 'IssueMetrics' | 'Issues' | 'HardwareStatus' | 'AlertsAndDrafts';

export function LocationLandingPage({ location }: LocationLandingPageProps) {
    const userEmail = useUser().user?.Email;
    const {
        data: metrics,
        isLoading,
        isFetching,
        error,
        refetch: refetchMetrics,
    } = useLocationIssueMetrics(location.Id);

    const [loadingWidgets, setLoadingWidgets] = useState<Record<WidgetName, boolean>>({
        IssueMetrics: false,
        Issues: false,
        HardwareStatus: false,
        AlertsAndDrafts: false,
    });

    const issuesWidgetRef = useRef<LocationIssuesWidgetRef>(null);
    const hardwareStatusWidgetRef = useRef<LocationHardwareStatusWidgetRef>(null);
    const alertsAndDraftsWidgetRef = useRef<AlertsAndDraftsWidgetRef>(null);

    const handleRefetch = useCallback(() => {
        refetchMetrics();
        setLoadingWidgets((prev) => ({
            ...prev,
            IssueMetrics: true,
            Issues: true,
            HardwareStatus: true,
            AlertsAndDrafts: true,
        }));

        // Create an array of promises for all widget refetches
        const refetchPromises = [
            issuesWidgetRef.current?.refetch(),
            hardwareStatusWidgetRef.current?.refetch(),
            alertsAndDraftsWidgetRef.current?.refetch(),
        ].filter(Boolean);

        // When all refetches complete, update loading states
        Promise.all(refetchPromises).finally(() =>
            setLoadingWidgets((prev) => ({
                ...prev,
                IssueMetrics: false,
                Issues: false,
                HardwareStatus: false,
                AlertsAndDrafts: false,
            })),
        );
    }, [refetchMetrics]);

    const pageTitle = useMemo(() => `${location.Name} Overview`, [location.Name]);

    // Helper to check if any widget is loading
    const isAnyWidgetLoading = Object.values(loadingWidgets).some(Boolean);

    return (
        <>
            <DocumentTitle title={pageTitle} />
            <BaseLandingPage>
                <TopRow>
                    <LandingPageLastUpdated>
                        <LastUpdated
                            isLoading={isFetching || isAnyWidgetLoading}
                            reload={handleRefetch}
                        />
                    </LandingPageLastUpdated>

                    <IssueMetrics
                        locationId={location.Id}
                        metrics={metrics ?? null}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        error={error instanceof Error ? error : null}
                    />
                </TopRow>

                <LocationOverviewStoreProvider
                    locationId={location.Id}
                    userEmail={userEmail ?? ''}
                >
                    <MainContent>
                        <LocationLeftColumn>
                            <LocationIssuesWidget
                                location={location}
                                ref={issuesWidgetRef}
                            />

                            <LocationHardwareStatusWidget
                                location={location}
                                ref={hardwareStatusWidgetRef}
                            />
                        </LocationLeftColumn>

                        <LocationRightColumn>
                            <AlertsAndDraftsWidget
                                location={location}
                                ref={alertsAndDraftsWidgetRef}
                            />
                        </LocationRightColumn>
                    </MainContent>
                </LocationOverviewStoreProvider>
            </BaseLandingPage>
        </>
    );
}
